import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft, KeyRound, Loader2, Mail, ShieldAlert, UserX, UserCheck, Camera, Plus, FileUp, BadgeCheck,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import RoleBadge from "@/components/RoleBadge";
import StaffAvatar from "@/components/StaffAvatar";
import { ComplianceBadge } from "@/pages/StaffList";
import { useAuth } from "@/context/AuthContext";
import SpotChecksPanel from "@/components/SpotChecksPanel";
import api, { formatApiError } from "@/lib/api";
import Forbidden from "@/pages/Forbidden";

const EXTRA_PERMISSIONS = [
  { key: "children:read_all", label: "View all children (not just allocated)" },
  { key: "audit:read", label: "View audit log" },
  { key: "staff:read", label: "View staff directory" },
  { key: "councils:read", label: "View local authorities" },
  { key: "schools:read", label: "View schools" },
];

const COMPLIANCE_TYPE_LABELS = {
  enhanced_dbs: "Enhanced DBS",
  safeguarding_training: "Safeguarding Training",
  first_aid: "First Aid",
  send_training: "SEND Training",
  itt_competency: "Independent Travel Training Competency",
  other: "Other",
};

const EMPLOYMENT_LABELS = {
  active: "Active", inactive: "Inactive", suspended: "Suspended", left_organisation: "Left organisation",
};

const EMPTY_COMPLIANCE = {
  type: "enhanced_dbs", label: "", not_applicable: false,
  completion_date: "", expiry_date: "", alert_days: "", notes: "",
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return format(new Date(v), "dd MMM yyyy");
  } catch {
    return v;
  }
};
const fmtDateTime = (v) => {
  if (!v) return "Never";
  try {
    return format(new Date(v), "dd MMM yyyy, HH:mm");
  } catch {
    return "—";
  }
};

export default function StaffDetail() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const isSelf = user?.id === id;
  const canRead = hasPermission("staff:read");
  const canUpdate = hasPermission("staff:update");
  const canManageRoles = hasPermission("roles:manage");
  const canDeactivate = hasPermission("staff:deactivate");
  const canReset = hasPermission("staff:reset_password");
  const canManageCompliance = hasPermission("compliance:manage");
  const canVerifyCompliance = hasPermission("compliance:verify");
  const canReadCompliance = hasPermission("compliance:read_all") || isSelf;
  const canSeeSpotChecks = hasPermission("spotchecks:read");

  const [staff, setStaff] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState(null);
  const [accessForm, setAccessForm] = useState({ role: "", extra_permissions: [] });
  const [compliance, setCompliance] = useState(null);
  const [complianceDialog, setComplianceDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [complianceForm, setComplianceForm] = useState(EMPTY_COMPLIANCE);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const photoInputRef = useRef(null);
  const evidenceInputRef = useRef(null);
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [managers, setManagers] = useState([]);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const saveEmail = async () => {
    setEmailSaving(true);
    try {
      const { data } = await api.patch(`/staff/${id}/email`, { email: emailForm.trim() });
      toast.success("Sign-in email updated — they must use the new email at next login");
      setEmailDialog(false);
      setStaff((s) => ({ ...s, email: data.email }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/${id}`);
      setStaff(data);
      setProfileForm({
        first_name: data.profile?.first_name || "",
        middle_name: data.profile?.middle_name || "",
        surname: data.profile?.surname || "",
        preferred_name: data.profile?.preferred_name || "",
        phone: data.profile?.phone || "",
        job_title: data.profile?.job_title || "",
        employment_status: data.profile?.employment_status || "active",
        start_date: data.profile?.start_date || "",
        end_date: data.profile?.end_date || "",
        emergency_contact_name: data.profile?.emergency_contact_name || "",
        emergency_contact_phone: data.profile?.emergency_contact_phone || "",
        manager_id: data.profile?.manager_id || "",
        dbs_status: data.profile?.dbs_status || "pending",
        dbs_expiry_date: data.profile?.dbs_expiry_date || "",
        notes: data.profile?.notes || "",
      });
      setAccessForm({ role: data.role, extra_permissions: data.extra_permissions || [] });
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCompliance = useCallback(async () => {
    if (!canReadCompliance) return;
    try {
      const { data } = await api.get(`/compliance/staff/${id}`);
      setCompliance(data);
    } catch {
      setCompliance(null);
    }
  }, [id, canReadCompliance]);

  useEffect(() => {
    load();
    loadCompliance();
  }, [load, loadCompliance]);

  useEffect(() => {
    if (canUpdate) {
      api.get("/staff", { params: { role: "admin_manager" } }).then((r) => setManagers(r.data.items)).catch(() => {});
    }
  }, [canUpdate]);

  if (!canRead && !isSelf) return <Forbidden />;
  if (denied) return <Forbidden />;
  if (loading || !profileForm) {
    return (
      <div className="flex items-center gap-2 text-slate-600" data-testid="staff-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading staff member…
      </div>
    );
  }
  if (!staff) return <p className="text-sm text-slate-600" data-testid="staff-detail-not-found">Staff member not found.</p>;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/staff/${id}`, profileForm);
      setStaff((s) => ({ ...s, ...data }));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAccess = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/staff/${id}/access`, accessForm);
      setStaff((s) => ({ ...s, ...data }));
      toast.success("Access updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const postAction = async (action, successMsg) => {
    setActing(true);
    try {
      await api.post(`/staff/${id}/${action}`);
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing(false);
    }
  };

  const uploadFile = async (file, category, complianceRecordId) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("staff_id", id);
    if (complianceRecordId) fd.append("compliance_record_id", complianceRecordId);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(category === "staff_photo" ? "Photograph updated" : "Evidence uploaded");
      load();
      loadCompliance();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCompliance = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: complianceForm.type,
        label: complianceForm.label || null,
        not_applicable: complianceForm.not_applicable,
        completion_date: complianceForm.completion_date || null,
        expiry_date: complianceForm.expiry_date || null,
        alert_days: complianceForm.alert_days ? parseInt(complianceForm.alert_days, 10) : null,
        notes: complianceForm.notes || null,
      };
      if (editingRecord) {
        await api.patch(`/compliance/${editingRecord.id}`, payload);
        toast.success("Compliance record updated");
      } else {
        await api.post(`/compliance/staff/${id}`, payload);
        toast.success("Compliance record added");
      }
      setComplianceDialog(false);
      setEditingRecord(null);
      setComplianceForm(EMPTY_COMPLIANCE);
      loadCompliance();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const verifyRecord = async (recordId, verified) => {
    try {
      await api.post(`/compliance/${recordId}/verify`, { verified });
      toast.success(verified ? "Record verified" : "Verification removed");
      loadCompliance();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEditCompliance = (record) => {
    setEditingRecord(record);
    setComplianceForm({
      type: record.type,
      label: record.label || "",
      not_applicable: record.not_applicable || false,
      completion_date: record.completion_date || "",
      expiry_date: record.expiry_date || "",
      alert_days: record.alert_days ? String(record.alert_days) : "",
      notes: record.notes || "",
    });
    setComplianceDialog(true);
  };

  return (
    <div data-testid="staff-detail-page" className="space-y-6">
      <div>
        <Link to="/staff" data-testid="back-to-staff-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-3">
          <ArrowLeft className="h-4 w-4" /> Back to staff
        </Link>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <StaffAvatar photoId={staff.profile?.photo_document_id} name={staff.name} size="h-20 w-20" testid="staff-detail-photo" />
            {(canUpdate || isSelf) && (
              <button
                data-testid="staff-photo-upload-button"
                onClick={() => photoInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow hover:bg-brand-hover transition-colors duration-200"
                aria-label="Upload photograph"
              >
                <Camera className="h-4 w-4" />
              </button>
            )}
            <input
              ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="staff-photo-input"
              onChange={(e) => uploadFile(e.target.files?.[0], "staff_photo")}
            />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{staff.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <RoleBadge role={staff.role} testid="staff-detail-role-badge" />
              <Badge variant="secondary" className="font-mono text-xs" data-testid="staff-detail-id">{staff.staff_number}</Badge>
              <Badge variant="secondary" data-testid="staff-detail-employment">{EMPLOYMENT_LABELS[staff.profile?.employment_status || "active"]}</Badge>
              {staff.status === "active" ? (
                <Badge className="bg-ukgreen-light text-ukgreen border-transparent" data-testid="staff-detail-status">Account active</Badge>
              ) : (
                <Badge className="bg-red-50 text-red-700 border-transparent" data-testid="staff-detail-status">Account deactivated</Badge>
              )}
              <ComplianceBadge status={staff.compliance_status} testid="staff-detail-compliance" />
            </div>
            <p className="text-sm text-slate-600 mt-1">{staff.email}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          {canReadCompliance && <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>}
          {canSeeSpotChecks && <TabsTrigger value="spotchecks" data-testid="tab-spotchecks">Spot Checks</TabsTrigger>}
          {(canManageRoles || canDeactivate || canReset) && !isSelf && (
            <TabsTrigger value="access" data-testid="tab-access">Access &amp; Security</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-lg">Staff profile</CardTitle></CardHeader>
              <CardContent>
                {canUpdate ? (
                  <form onSubmit={saveProfile} className="space-y-4" data-testid="staff-profile-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5"><Label>First name</Label>
                        <Input data-testid="staff-edit-firstname-input" value={profileForm.first_name} onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Surname</Label>
                        <Input data-testid="staff-edit-surname-input" value={profileForm.surname} onChange={(e) => setProfileForm({ ...profileForm, surname: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Middle name</Label>
                        <Input data-testid="staff-edit-middlename-input" value={profileForm.middle_name} onChange={(e) => setProfileForm({ ...profileForm, middle_name: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Preferred name</Label>
                        <Input data-testid="staff-edit-preferredname-input" value={profileForm.preferred_name} onChange={(e) => setProfileForm({ ...profileForm, preferred_name: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Telephone</Label>
                        <Input data-testid="staff-edit-phone-input" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Job title</Label>
                        <Input data-testid="staff-edit-jobtitle-input" value={profileForm.job_title} onChange={(e) => setProfileForm({ ...profileForm, job_title: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Employment status</Label>
                        <Select value={profileForm.employment_status} onValueChange={(v) => setProfileForm({ ...profileForm, employment_status: v })}>
                          <SelectTrigger data-testid="staff-edit-employment-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {Object.entries(EMPLOYMENT_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`employment-option-${k}`}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Setting Inactive, Suspended or Left Organisation also deactivates login access. Records are never deleted.</p>
                      </div>
                      <div className="space-y-1.5"><Label>Manager</Label>
                        <Select value={profileForm.manager_id || "none"} onValueChange={(v) => setProfileForm({ ...profileForm, manager_id: v === "none" ? "" : v })}>
                          <SelectTrigger data-testid="staff-edit-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="none">No manager</SelectItem>
                            {managers.filter((m) => m.id !== id).map((m) => (
                              <SelectItem key={m.id} value={m.id} data-testid={`manager-option-${m.staff_number}`}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label>Start date</Label>
                        <Input data-testid="staff-edit-startdate-input" type="date" value={profileForm.start_date} onChange={(e) => setProfileForm({ ...profileForm, start_date: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>End date</Label>
                        <Input data-testid="staff-edit-enddate-input" type="date" value={profileForm.end_date} onChange={(e) => setProfileForm({ ...profileForm, end_date: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Emergency contact name</Label>
                        <Input data-testid="staff-edit-emergency-name-input" value={profileForm.emergency_contact_name} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_name: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Emergency contact phone</Label>
                        <Input data-testid="staff-edit-emergency-phone-input" value={profileForm.emergency_contact_phone} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_phone: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Manager notes (restricted — not visible to the staff member)</Label>
                      <Textarea data-testid="staff-edit-notes-input" rows={3} value={profileForm.notes} onChange={(e) => setProfileForm({ ...profileForm, notes: e.target.value })} />
                    </div>
                    <Button data-testid="staff-profile-save-button" type="submit" disabled={saving}
                      className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}
                    </Button>
                  </form>
                ) : (
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-slate-500">Telephone</dt><dd className="font-medium text-slate-800">{staff.profile?.phone || "—"}</dd></div>
                    <div><dt className="text-slate-500">Job title</dt><dd className="font-medium text-slate-800">{staff.profile?.job_title || "—"}</dd></div>
                    <div><dt className="text-slate-500">Start date</dt><dd className="font-medium text-slate-800">{fmtDate(staff.profile?.start_date)}</dd></div>
                    <div><dt className="text-slate-500">Manager</dt><dd className="font-medium text-slate-800">{staff.manager_name || "—"}</dd></div>
                  </dl>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-lg">Record summary</CardTitle></CardHeader>
                <CardContent>
                  <dl className="space-y-3 text-sm">
                    <div><dt className="text-slate-500">Staff ID</dt><dd className="font-mono font-medium" data-testid="summary-staff-id">{staff.staff_number}</dd></div>
                    <div><dt className="text-slate-500">Manager</dt><dd className="font-medium" data-testid="summary-manager">{staff.manager_name || "—"}</dd></div>
                    <div><dt className="text-slate-500">Last sign-in</dt><dd className="font-medium">{fmtDateTime(staff.last_login_at)}</dd></div>
                    <div><dt className="text-slate-500">Account created</dt><dd className="font-medium">{fmtDateTime(staff.created_at)}</dd></div>
                    <div><dt className="text-slate-500">Assigned cases</dt><dd className="font-medium" data-testid="summary-assigned-cases">{staff.assigned_cases ?? 0}</dd></div>
                  </dl>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Assigned cases</CardTitle></CardHeader>
                <CardContent>
                  {staff.assigned_children?.length ? (
                    <ul className="space-y-1 text-sm">
                      {staff.assigned_children.map((c) => <li key={c.id}>{c.first_name} {c.last_name}</li>)}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-600" data-testid="assigned-cases-empty">
                      No children allocated. Case allocation arrives with the Children module (Phase 3).
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {canReadCompliance && (
          <TabsContent value="compliance" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Staff compliance</CardTitle>
                {canManageCompliance && (
                  <Button data-testid="compliance-add-button" onClick={() => { setEditingRecord(null); setComplianceForm(EMPTY_COMPLIANCE); setComplianceDialog(true); }}
                    className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold">
                    <Plus className="h-4 w-4 mr-2" /> Add record
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {compliance?.missing_required?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="compliance-missing-alert">
                    Missing required records: {compliance.missing_required.map((t) => COMPLIANCE_TYPE_LABELS[t] || t).join(", ")}
                  </div>
                )}
                {!compliance ? (
                  <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : compliance.items.length === 0 ? (
                  <p className="text-sm text-slate-600" data-testid="compliance-empty">No compliance records yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="compliance-list">
                    {compliance.items.map((r) => (
                      <div key={r.id} data-testid={`compliance-record-${r.id}`}
                        className="rounded-md border border-border p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-slate-900">{COMPLIANCE_TYPE_LABELS[r.type] || r.type}{r.label ? ` — ${r.label}` : ""}</p>
                            <ComplianceBadge status={r.status} testid={`compliance-status-${r.id}`} />
                            {r.verified && (
                              <Badge className="bg-ukgreen-light text-ukgreen border-transparent" data-testid={`compliance-verified-${r.id}`}>
                                <BadgeCheck className="h-3 w-3 mr-1" /> Verified
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Completed: {fmtDate(r.completion_date)} · Expires: {fmtDate(r.expiry_date)}
                            {r.verified_at ? ` · Verified ${fmtDate(r.verified_at)}` : ""}
                          </p>
                          {r.notes && <p className="text-xs text-slate-600 mt-1">{r.notes}</p>}
                          {r.evidence_document_id && (
                            <a
                              data-testid={`compliance-evidence-${r.id}`}
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.evidence_document_id}/download`}
                              target="_blank" rel="noreferrer"
                              className="mt-1 inline-block text-xs font-medium text-primary hover:underline"
                            >
                              View evidence document
                            </a>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {canManageCompliance && (
                            <>
                              <Button variant="outline" className="h-10" data-testid={`compliance-edit-${r.id}`} onClick={() => openEditCompliance(r)}>Edit</Button>
                              <Button variant="outline" className="h-10" data-testid={`compliance-upload-${r.id}`}
                                onClick={() => { setEvidenceTarget(r.id); evidenceInputRef.current?.click(); }}>
                                <FileUp className="h-4 w-4 mr-1" /> Evidence
                              </Button>
                            </>
                          )}
                          {canVerifyCompliance && (
                            r.verified ? (
                              <Button variant="outline" className="h-10" data-testid={`compliance-unverify-${r.id}`} onClick={() => verifyRecord(r.id, false)}>Unverify</Button>
                            ) : (
                              <Button variant="outline" className="h-10 border-green-300 text-ukgreen hover:bg-ukgreen-light" data-testid={`compliance-verify-${r.id}`} onClick={() => verifyRecord(r.id, true)}>Verify</Button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={evidenceInputRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="compliance-evidence-input"
                  onChange={(e) => { uploadFile(e.target.files?.[0], "compliance_evidence", evidenceTarget); e.target.value = ""; }} />
              </CardContent>
            </Card>

            <Dialog open={complianceDialog} onOpenChange={setComplianceDialog}>
              <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">{editingRecord ? "Edit compliance record" : "Add compliance record"}</DialogTitle>
                  <DialogDescription>Requirement, dates, evidence and notes for this staff member.</DialogDescription>
                </DialogHeader>
                <form onSubmit={saveCompliance} className="space-y-4" data-testid="compliance-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Requirement / type</Label>
                      <Select value={complianceForm.type} onValueChange={(v) => setComplianceForm({ ...complianceForm, type: v })}>
                        <SelectTrigger data-testid="compliance-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {Object.entries(COMPLIANCE_TYPE_LABELS).map(([k, l]) => (
                            <SelectItem key={k} value={k} data-testid={`compliance-type-${k}`}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {complianceForm.type === "other" && (
                      <div className="space-y-1.5"><Label>Custom label</Label>
                        <Input data-testid="compliance-label-input" value={complianceForm.label} onChange={(e) => setComplianceForm({ ...complianceForm, label: e.target.value })} /></div>
                    )}
                    <div className="space-y-1.5"><Label>Completion date</Label>
                      <Input data-testid="compliance-completion-input" type="date" value={complianceForm.completion_date} onChange={(e) => setComplianceForm({ ...complianceForm, completion_date: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Expiry / renewal date</Label>
                      <Input data-testid="compliance-expiry-input" type="date" value={complianceForm.expiry_date} onChange={(e) => setComplianceForm({ ...complianceForm, expiry_date: e.target.value })} />
                      <p className="text-xs text-slate-500">Leave blank if the requirement has no fixed expiry.</p></div>
                    <div className="space-y-1.5"><Label>Alert days before expiry (optional)</Label>
                      <Input data-testid="compliance-alertdays-input" type="number" min="1" max="365" value={complianceForm.alert_days} onChange={(e) => setComplianceForm({ ...complianceForm, alert_days: e.target.value })} /></div>
                    <div className="flex items-center gap-2 pt-6 min-h-[44px]">
                      <Checkbox data-testid="compliance-na-checkbox" checked={complianceForm.not_applicable}
                        onCheckedChange={(v) => setComplianceForm({ ...complianceForm, not_applicable: Boolean(v) })} />
                      <Label className="text-sm">Not applicable to this staff member</Label>
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>Notes</Label>
                    <Textarea data-testid="compliance-notes-input" rows={2} value={complianceForm.notes} onChange={(e) => setComplianceForm({ ...complianceForm, notes: e.target.value })} /></div>
                  <DialogFooter>
                    <Button data-testid="compliance-save-button" type="submit" disabled={saving}
                      className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingRecord ? "Save changes" : "Add record"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {(canManageRoles || canDeactivate || canReset) && !isSelf && (
          <TabsContent value="access" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {canManageRoles && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShieldAlert className="h-5 w-5 text-primary" /> Role &amp; permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={saveAccess} className="space-y-4" data-testid="staff-access-form">
                      <div className="space-y-1.5">
                        <Label>Role</Label>
                        <Select value={accessForm.role} onValueChange={(v) => setAccessForm({ role: v, extra_permissions: [] })}>
                          <SelectTrigger data-testid="staff-access-role-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="travel_trainer">Travel Trainer</SelectItem>
                            <SelectItem value="admin_manager">Co-ordinator</SelectItem>
                            <SelectItem value="super_admin">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Changing the role clears any extra permissions.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Extra permissions (granted individually)</Label>
                        {EXTRA_PERMISSIONS.map((p) => (
                          <label key={p.key} className="flex items-center gap-2 text-sm text-slate-700 min-h-[44px]">
                            <Checkbox data-testid={`extra-perm-${p.key}`}
                              checked={accessForm.extra_permissions.includes(p.key)}
                              onCheckedChange={() =>
                                setAccessForm((f) => ({
                                  ...f,
                                  extra_permissions: f.extra_permissions.includes(p.key)
                                    ? f.extra_permissions.filter((x) => x !== p.key)
                                    : [...f.extra_permissions, p.key],
                                }))
                              }
                            />
                            {p.label}
                          </label>
                        ))}
                      </div>
                      <Button data-testid="staff-access-save-button" type="submit" disabled={saving}
                        className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-4">
                <Card>
                  <CardHeader><CardTitle className="text-lg">Account actions</CardTitle></CardHeader>
                  <CardContent className="flex flex-wrap gap-3">
                    {canReset && (
                      <Button data-testid="staff-send-reset-button" variant="outline" disabled={acting}
                        onClick={() => postAction("send-password-reset", "Password reset email sent")} className="h-11">
                        <KeyRound className="h-4 w-4 mr-2" /> Send password reset
                      </Button>
                    )}
                    {canDeactivate && (staff.status === "active" ? (
                      <Button data-testid="staff-deactivate-button" variant="outline" disabled={acting}
                        onClick={() => postAction("deactivate", "Account deactivated")}
                        className="h-11 border-red-300 text-red-700 hover:bg-red-50">
                        <UserX className="h-4 w-4 mr-2" /> Deactivate account
                      </Button>
                    ) : (
                      <Button data-testid="staff-reactivate-button" variant="outline" disabled={acting}
                        onClick={() => postAction("reactivate", "Account reactivated")}
                        className="h-11 border-green-300 text-ukgreen hover:bg-ukgreen-light">
                        <UserCheck className="h-4 w-4 mr-2" /> Reactivate account
                      </Button>
                    ))}
                    {canManageRoles && (
                      <Button data-testid="staff-correct-email-button" variant="outline" disabled={acting}
                        onClick={() => { setEmailForm(staff.email || ""); setEmailDialog(true); }} className="h-11">
                        <Mail className="h-4 w-4 mr-2" /> Correct sign-in email
                      </Button>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-lg">Security</CardTitle></CardHeader>
                  <CardContent className="text-sm text-slate-600 space-y-2">
                    <p data-testid="mfa-status">Multi-factor authentication: {staff.mfa_enabled ? "Enabled" : "Not yet enabled (platform ready)"}</p>
                    <p>Deactivation disables login while preserving the staff record, audit history and all previously authored work.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        )}
          {canSeeSpotChecks && (
            <TabsContent value="spotchecks" className="mt-4"><SpotChecksPanel staffId={id} /></TabsContent>
          )}
      </Tabs>

      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="bg-white" data-testid="correct-email-dialog">
          <DialogHeader>
            <DialogTitle>Correct sign-in email</DialogTitle>
            <DialogDescription>
              This changes the email {staff?.name} uses to sign in. They must use the new address at their next login.
              Active sessions are signed out. Recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="correct-email">New email address</Label>
            <Input id="correct-email" data-testid="correct-email-input" type="email" value={emailForm}
              onChange={(e) => setEmailForm(e.target.value)} placeholder="name@example.com" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)}>Cancel</Button>
            <Button data-testid="correct-email-save-button" disabled={emailSaving || !emailForm.trim()} onClick={saveEmail}
              className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {emailSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
